import AppFooter from '../components/AppFooter';
import Breadcrumbs from '../components/Breadcrumbs';
import PageMeta from '../components/PageMeta';

export default function PrivacyPolicy() {
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900"
    >
      <PageMeta
        title="Privacy Policy"
        description="Romy's privacy policy. Learn how we collect, use, and protect your health data with industry-leading security practices."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Breadcrumbs current="Privacy Policy" />

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">Romy &ndash; Privacy Policy</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-8"><strong>Last Updated:</strong> July 2026</p>

        <div className="prose prose-gray max-w-none space-y-6">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            This Privacy Policy (&ldquo;Policy&rdquo;) describes how Researcher LLC DBA Romy AI (&ldquo;Company,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) collects, uses, discloses, stores, and protects personal information in connection with your use of the Romy AI chatbot (&ldquo;Romy&rdquo; or &ldquo;Service&rdquo;).
          </p>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
            By accessing or using Romy, you acknowledge that you have read, understood, and agree to this Privacy Policy. If you do not agree, do not use the Service.
          </p>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">1. Scope and Relationship to Terms</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              This Privacy Policy forms part of and is incorporated into the Romy Terms and Conditions. By using Romy, you agree to both this Policy and the Terms.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">2. Information We Collect</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We collect information that you voluntarily provide, information generated through your use of the Service, and information collected automatically.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">2.1 Information You Provide</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">You may provide personal information when you:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
              <li>Create an account;</li>
              <li>Communicate with Romy;</li>
              <li>Submit health-related information;</li>
              <li>Participate in surveys or research;</li>
              <li>Contact support;</li>
              <li>Provide feedback.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">This may include:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-4">
              <li>Name, email address, and account credentials;</li>
              <li>Demographic information;</li>
              <li>Health-related information and personal experiences;</li>
              <li>Messages, chat transcripts, and interactions;</li>
              <li>Voice recordings, when you choose to use the microphone button to dictate a message. Audio is transmitted to our infrastructure provider for speech-to-text transcription (see Section 5.1) and is not retained by Romy after transcription;</li>
              <li>Any other information you choose to submit.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">2.2 Automatically Collected Information</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">When you use Romy, we may automatically collect:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-4">
              <li>IP address;</li>
              <li>Device identifiers;</li>
              <li>Browser type;</li>
              <li>Operating system;</li>
              <li>Usage data and interaction patterns;</li>
              <li>Log files;</li>
              <li>Cookies and similar technologies.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">2.3 Sensitive and Health-Related Information</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You acknowledge that Romy may collect sensitive personal and health-related information that you voluntarily disclose. You expressly consent to our collection, processing, and use of this information as described in this Policy.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">We use collected information for the following purposes:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1">
              <li>To operate, maintain, and improve Romy;</li>
              <li>To personalize user experience;</li>
              <li>To conduct research and analytics;</li>
              <li>To generate aggregated insights;</li>
              <li>To communicate with you;</li>
              <li>To provide customer support;</li>
              <li>To detect fraud and misuse;</li>
              <li>To comply with legal obligations;</li>
              <li>To enforce our Terms.</li>
            </ul>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">4. Data Anonymization, Aggregation, and Commercial Use</h2>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">4.1 Anonymization</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We may remove or modify personally identifiable information to create anonymized datasets that cannot reasonably be used to identify you.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">4.2 Aggregated Data</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We may combine data from multiple users to produce aggregated datasets, statistical analyses, and research outputs.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">4.3 Sale and Licensing of Data</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">You acknowledge and agree that the Company may:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
              <li>Sell;</li>
              <li>License;</li>
              <li>Transfer;</li>
              <li>Share; and/or</li>
              <li>Commercialize</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              anonymized and aggregated user data with third parties, including research institutions, healthcare organizations, pharmaceutical companies, medical device companies, academic institutions, and commercial partners.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Such data will not reasonably identify you individually.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              By using Romy, you expressly consent to this anonymization and commercialization of your data.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">5. Disclosure of Information</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">We may disclose information in the following circumstances:</p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">5.1 Service Providers</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We may share information with third-party vendors, contractors, and service providers who perform services on our behalf (e.g., hosting, analytics, security, customer support). Key infrastructure providers include:
            </p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-4">
              <li><strong>Google Cloud Platform</strong> — application hosting, database, file storage, and AI/ML services. When you use voice input, audio is sent to Google Cloud Speech-to-Text for transcription. When you chat with Romy, message content is sent to Google Vertex AI for response generation. Google does not use this data to train its general-purpose models when accessed through these enterprise services.</li>
              <li><strong>Firebase</strong> (a Google service) — authentication, push notifications, and analytics.</li>
              <li><strong>Sentry</strong> — error monitoring and crash reporting.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              These providers are contractually obligated to handle your information in accordance with this Policy and applicable law.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">5.2 Research and Commercial Partners</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We may share anonymized or aggregated data with research partners and commercial collaborators.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">5.3 Legal and Regulatory Requirements</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We may disclose information if required to do so by law, regulation, court order, or governmental request.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">5.4 Business Transfers</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              If we are involved in a merger, acquisition, financing, reorganization, or sale of assets, your information may be transferred as part of that transaction.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-3">5.5 Protection of Rights</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We may disclose information to protect our rights, safety, property, users, or the public.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">6. Data Retention</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We retain personal information for as long as necessary to fulfill the purposes described in this Policy, unless a longer retention period is required or permitted by law.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We may retain anonymized or aggregated data indefinitely.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">7. Data Security</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We implement commercially reasonable administrative, technical, and physical safeguards to protect your information. However, no security system is impenetrable.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Access to your personal and health-related information is limited to authorized personnel and service providers who need it to operate, support, secure, troubleshoot, and improve the Service, or to comply with legal obligations. This means that, in these limited circumstances, our staff or contractors may be able to view information you have provided, including health-related information. We restrict such access to what is reasonably necessary and require those with access to protect your information.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You acknowledge that transmission and storage of data involves inherent risks.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">8. Your Rights and Choices</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Depending on your jurisdiction, you may have rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
              <li>Access;</li>
              <li>Correction;</li>
              <li>Deletion;</li>
              <li>Restriction;</li>
              <li>Portability;</li>
              <li>Objection to processing.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              You may submit requests by contacting us at the email address below. We reserve the right to verify your identity before responding.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Certain rights may be limited where data has been anonymized or aggregated.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">9. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We may use cookies, pixels, and similar technologies to collect usage information and improve functionality.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              You may manage cookie preferences through your browser settings.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">10. Children&apos;s Privacy</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Romy is not directed to children under 13. We do not knowingly collect personal information from anyone under the age of 13.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              If we learn that we have collected personal information from a child under 13, we will delete that information promptly. If you believe a child under 13 has provided us with personal information, please contact us at the email address below.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">11. International Data Transfers</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Your information may be transferred to and processed in countries other than your own, including the United States, where data protection laws may differ.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              By using Romy, you consent to such transfers.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">12. Third-Party Links and Services</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Romy may contain links to third-party websites or services. We are not responsible for their privacy practices.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We encourage you to review their privacy policies.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">13. Changes to This Policy</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We may update this Privacy Policy at any time. Revisions will be posted with a new &ldquo;Last Updated&rdquo; date.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Continued use of Romy after changes constitutes acceptance of the revised Policy.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">14. Governing Law</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              This Privacy Policy is governed by the laws of the State of Delaware, without regard to conflict-of-law principles.
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">15. Contact Information</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
              If you have questions or concerns about this Policy, please contact:
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Researcher LLC DBA Romy AI<br />
              16192 Coastal Highway, Lewes, Delaware 19958<br />
              Phone:{' '}
              <a href="tel:+16086160236" className="text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 underline">
                (608) 616-0236
              </a><br />
              Email:{' '}
              <a href="mailto:hello@romyhealth.co" className="text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 underline">
                hello@romyhealth.co
              </a>
            </p>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">16. Consent and Acknowledgment</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">By using Romy, you acknowledge and agree that:</p>
            <ul className="list-disc pl-5 text-gray-700 dark:text-gray-300 space-y-1 mb-3">
              <li>We may collect sensitive and health-related information;</li>
              <li>We may anonymize, aggregate, and commercialize such data;</li>
              <li>We may share anonymized data with third parties;</li>
              <li>Your use constitutes informed consent to these practices.</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Your continued use of Romy constitutes your electronic signature and acceptance of this Privacy Policy.
            </p>
          </section>
        </div>

      </div>

      <AppFooter />
    </div>
  );
}
